import {
  postAssignmentDataSourceProbe,
  type Input as PostAssignmentDataSourceProbeInput,
} from '@gonfalon/openapi/operations/postAssignmentDataSourceProbe';
import type { CustomAssignmentSourceProbeRep } from '@gonfalon/openapi/types';

const probeStatuses = new Set(['ok', 'warn', 'blocked']);

export function isAssignmentSourceProbeRep(value: unknown): value is CustomAssignmentSourceProbeRep {
  if (value === null || typeof value !== 'object' || !('status' in value)) {
    return false;
  }

  return typeof value.status === 'string' && probeStatuses.has(value.status);
}

type AssignmentSourceProbeFetchResult = Awaited<ReturnType<typeof postAssignmentDataSourceProbe>>;

/**
 * HTTP 422 with a probe body is a classified verdict (blocked), not a transport failure.
 * Lift that body onto `data` with a 200 so `reactQueryResponseAdapter` keeps `blockMessage`.
 */
export function mapAssignmentSourceProbeResponse(
  result: AssignmentSourceProbeFetchResult,
): AssignmentSourceProbeFetchResult {
  if (result.response.status !== 422) {
    return result;
  }

  const probeRep = isAssignmentSourceProbeRep(result.error)
    ? result.error
    : isAssignmentSourceProbeRep(result.data)
      ? result.data
      : undefined;
  if (!probeRep) {
    return result;
  }

  return {
    data: probeRep,
    error: undefined,
    response: new Response(JSON.stringify(probeRep), { status: 200 }),
  };
}

export async function postAssignmentDataSourceProbeForQuery(
  input: PostAssignmentDataSourceProbeInput,
  context: { signal?: AbortSignal; headers?: HeadersInit } = {},
) {
  return mapAssignmentSourceProbeResponse(await postAssignmentDataSourceProbe(input, context));
}
